import React, { useEffect, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useQuery } from 'react-query';
import { Flex, Form, Layout, Spin } from 'antd';
import { Button, Radio, Space, InputText, Title } from '@g2u/ui-kit';
import { SearchOutlined } from '@ant-design/icons';
import cx from 'classnames';

import { BookingProgress, Breadcrumb, CustomerTime } from 'components';
import { type JsonDataItem, type WithOptionalDataTest } from 'types';
import { Toast, addSpacesToNumber, parseErrors } from 'helpers';
import { getCallScripts } from 'queries';
import { BookingStore, RootStore, ScriptStore } from 'store';
import styles from './header.module.scss';
import useUserInfo from 'hooks/useUserInfo';

export namespace Header {
  export type Props = WithOptionalDataTest;

  enum SearchBy {
    Customer = '/customer/search',
    Job = '/job/search',
  }

  export const $: FC<Props> = observer(() => {
    const { breadcrumbs, isTrainingMode } = RootStore;
    const { setScript } = ScriptStore;
    const { step } = BookingStore;
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const searchType = Form.useWatch('searchType', { form });
    const searchValue = Form.useWatch('searchValue', { form });

    const [user, isUserLoading] = useUserInfo();
    const { isLoading: isScriptsLoading } = useQuery('get-scripts', getCallScripts, {
      onSuccess: (data) => {
        setScript(data);
      },
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    });

    useEffect(() => {
      if (searchType === SearchBy.Job) form.setFieldValue('searchValue', '');
    }, [searchType]);

    const handleSubmit = () => {
      form.setFieldValue('searchValue', '');
      navigate(searchType, {
        state: { searchValue },
      });
    };

    return (
      <Layout.Header className={cx(styles.header, isTrainingMode && styles.header_training)}>
        <Space.$ direction={Space.Direction.Vertical} fullWidth>
          {step > 0 ? (
            <BookingProgress.$ step={step} />
          ) : (
            <Flex className={styles.header__inner}>
              {breadcrumbs ? (
                <Breadcrumb.$ links={breadcrumbs} hasTime={false} />
              ) : (
                <Title.$ level={5} noMargin className={styles.header__user}>
                  {isUserLoading || isScriptsLoading ? <Spin /> : <>G’day, {user?.name.split(' ')[0]}!</>}
                </Title.$>
              )}
              <CustomerTime.$ />
              <Space.$ direction={Space.Direction.Horizontal} size={Space.Size.Middle}>
                <Form
                  form={form}
                  initialValues={{ searchType: SearchBy.Customer }}
                  onSubmitCapture={handleSubmit}
                  validateTrigger="onBlur"
                >
                  <Space.$ direction={Space.Direction.Horizontal} size={Space.Size.Middle}>
                    {searchType === SearchBy.Customer && (
                      <InputText.$ label="" name="searchValue" placeholder="" initialValue="" width={140} />
                    )}

                    {searchType === SearchBy.Job && (
                      <InputText.$
                        label=""
                        name="searchValue"
                        placeholder=""
                        initialValue=""
                        width={140}
                        maxLength={7}
                        formatter={(val) => addSpacesToNumber(val?.toString(), [])}
                      />
                    )}

                    <Radio.$
                      name="searchType"
                      label=" "
                      colon={false}
                      options={[
                        {
                          label: <>Customers</>,
                          value: SearchBy.Customer,
                        },
                        {
                          label: <>Jobs</>,
                          value: SearchBy.Job,
                        },
                      ]}
                    />
                  </Space.$>
                </Form>
                <Button.$
                  type={Button.Type.Primary}
                  icon={<SearchOutlined />}
                  onClick={handleSubmit}
                  dataTestId="search-profile-btn"
                >
                  Search
                </Button.$>
              </Space.$>
            </Flex>
          )}
        </Space.$>
      </Layout.Header>
    );
  });
}
